import React, { useState, useEffect } from "react";
import { Button, Card, Modal, Table, Form, Input, message, Select } from "antd";
import useRequest from "../../hooks/useRequest";
import ViewTableIcon from "../../assets/images/view-table-icon.svg";
import EditTableIcon from "../../assets/images/edit-icon-table.svg";
import DeleteTableIcon from "../../assets/images/delete-table-icon.svg";
import ModalDeleteIcon from "../../assets/images/delete-icon.svg";
import { ExclamationCircleOutlined } from "@ant-design/icons";

// CSS for unread question highlighting
const unreadRowStyle = `
  .unread-question-row {
    background-color: #fff3cd !important;
    border-left: 4px solid #ffc107 !important;
    font-weight: 500 !important;
  }
  
  .unread-question-row:hover {
    background-color: #ffeaa7 !important;
  }
  
  .unread-question-row .ant-table-cell {
    background-color: transparent !important;
  }
  
  .unread-question-row .question {
    font-weight: 600 !important;
  }
`;

const { TextArea } = Input;
const { confirm } = Modal;

const apiPath = {
  listQuestion: "business/question/list",
  addQuestion: "business/question/add-question",
  editQuestion: "business/question/edit-question",
  viewQuestion: "business/question/view-question",
  deleteQuestion: "business/question/delete-question",
};

const ListQuestionsIndex = () => {
  const [form] = Form.useForm();
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const { request } = useRequest();

  useEffect(() => {
    fetchQuestions();
  }, [pagination.current, pagination.pageSize]);

  // Inject CSS for unread question highlighting
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = unreadRowStyle;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const fetchQuestions = () => {
    setLoading(true);
    request({
      url: apiPath.listQuestion + `/?page=${pagination.current}&limit=${pagination.pageSize}`,
      method: "GET",
      // params: {
      //   page: pagination.current,
      //   limit: pagination.pageSize,
      // },
      onSuccess: (data) => {
        if (data.status) {
          setDataSource(data.data.docs || []);
          setPagination({
            ...pagination,
            total: data.data.totalDocs || 0,
          });
        } else {
          message.error(data.message || "Failed to fetch questions");
        }
        setLoading(false);
      },
      onError: (error) => {
        message.error(
          error?.message || "An error occurred while fetching questions"
        );
        setLoading(false);
      },
    });
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const viewQuestion = (id) => {
    setLoading(true);
    request({
      url: `${apiPath.viewQuestion}/${id}`,
      method: "PUT",
      onSuccess: (data) => {
        setLoading(false);
        if (data.status) {
          console.log(data.data.docs);
          setSelectedQuestion(data.data);
          setViewOpen(true);

          // Mark the question as read locally without calling the list API
          setDataSource(prevData =>
            prevData.map(item =>
              item._id === id
                ? { ...item, is_read: true }
                : item
            )
          );
        } else {
          message.error(data.message || "Failed to view question");
        }
      },
      onError: (error) => {
        setLoading(false);
        message.error(
          error?.message || "An error occurred while viewing the question"
        );
      },
    });
  };

  const handleEditQuestion = (record) => {
    if (!record || !record._id) {
      message.error("Invalid question record");
      return;
    }

    setSelectedQuestion(record);
    form.setFieldsValue({
      question: record.question,
      answer: record.answer,
    });
    setEditOpen(true);
  };

  const submitEditQuestion = (values) => {
    if (!selectedQuestion?._id) {
      message.error("Question ID is missing");
      return;
    }

    setLoading(true);
    request({
      url: `${apiPath.editQuestion}/${selectedQuestion._id}`,
      method: "PUT",
      data: {
        ...values,
      },
      onSuccess: (data) => {
        setLoading(false);
        if (data.status) {
          message.success(data.message || "Question updated successfully");
          setEditOpen(false);
          form.resetFields();
          fetchQuestions();
        } else {
          message.error(data.message || "Failed to update question");
        }
      },
      onError: (error) => {
        setLoading(false);
        message.error(
          error?.message || "An error occurred while updating the question"
        );
      },
    });
  };

  const handleChange = (value) => { };

  const showDeleteConfirm = (record) => {
    setSelectedQuestion(record);
    setDeleteOpen(true);
  };

  const handleDeleteQuestion = () => {
    if (!selectedQuestion?._id) {
      message.error("Question ID is missing");
      return;
    }

    setLoading(true);
    request({
      url: `${apiPath.deleteQuestion}/${selectedQuestion._id}`,
      method: "DELETE",
      onSuccess: (data) => {
        setLoading(false);
        if (data.status) {
          message.success(data.message || "Question deleted successfully");
          setDeleteOpen(false);
          fetchQuestions();
        } else {
          message.error(data.message || "Failed to delete question");
        }
      },
      onError: (error) => {
        setLoading(false);
        message.error(
          error?.message || "An error occurred while deleting the question"
        );
      },
    });
  };

  const columns = [
    {
      title: "S.NO",
      dataIndex: "sno",
      key: "sno",
      width: 80,
      render: (text, record, index) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span className="sno">
            {((pagination.current - 1) * pagination.pageSize + index + 1)
              .toString()
              .padStart(2, "0")}
          </span>
          {record.is_read === false && (
            <span
              style={{
                backgroundColor: '#ffc107',
                color: '#000',
                fontSize: '10px',
                padding: '2px 6px',
                borderRadius: '10px',
                fontWeight: 'bold',
                textTransform: 'uppercase'
              }}
            >
              New
            </span>
          )}
        </div>
      ),
    },
    {
      title: "QUESTIONS",
      dataIndex: "question",
      key: "question",
      render: (text, record) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {record.is_read === false && (
              <div
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#ffc107',
                  flexShrink: 0
                }}
              />
            )}
            <div>
              <p className="question">{text}</p>
              <p className="answer">{record.answer}</p>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "DATE",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      render: (text) => (
        <span className="date">{new Date(text).toLocaleDateString()}</span>
      ),
    },
    {
      title: "ACTION'S",
      key: "actions",
      width: 150,
      render: (record) => (
        <div className="actions">
          <Button
            className="view-btn"
            icon={<img src={ViewTableIcon} alt="View" />}
            onClick={() => viewQuestion(record._id)}
          />
          <Button
            className="edit-btn-table"
            icon={<img src={EditTableIcon} alt="Edit" />}
            onClick={() => handleEditQuestion(record)}
          />
          <Button
            className="delete-btn-table"
            icon={<img src={DeleteTableIcon} alt="Delete" />}
            onClick={() => showDeleteConfirm(record)}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <Card bordered={false} className="question-list-card">
        <div className="main-wht-head-outer">
          <div className="wht-card-headding">
            <h6>List of Questions</h6>
          </div>
          <div className="wht-right-headding">
            <Select
              defaultValue="Latest"
              style={{
                width: 120,
              }}
              onChange={handleChange}
              options={[
                {
                  value: "jack",
                  label: "Jack",
                },
                {
                  value: "Latest",
                  label: "Latest",
                },
                {
                  value: "Yiminghe",
                  label: "yiminghe",
                },
              ]}
            />
          </div>
        </div>
        <div className="table-responsive">
          <Table
            dataSource={dataSource}
            columns={columns}
            loading={loading}
            rowKey="_id"
            pagination={pagination}
            onChange={handleTableChange}
            rowClassName={(record) =>
              record.is_read === false ? 'unread-question-row' : ''
            }
          />
        </div>

        {/* View Question Modal */}
        <Modal
          title=""
          className="tab_modal"
          width={900}
          open={viewOpen}
          onCancel={() => setViewOpen(false)}
          footer={false}
        >
          {selectedQuestion && (
            <div className="layout-modal-body">
              {/* Question Number Header */}
              <div className="modal-headding">
                <h6>
                  {selectedQuestion?.sno
                    ? `${selectedQuestion.sno
                      .toString()
                      .padStart(2, "0")} Question`
                    : "Question"}
                </h6>
              </div>

              {/* Question & Options */}
              <div className="modal-question-list">
                <h6>
                  Q{selectedQuestion.sno}.
                  <span> {selectedQuestion.question} </span>
                </h6>

                {selectedQuestion.options?.map((option, index) => (
                  <h6 key={index}>
                    {String.fromCharCode(97 + index)}. {/* a, b, c, d */}
                    <span> {option} </span>
                  </h6>
                ))}

                <h6>
                  <strong>a:</strong>
                  <span> {selectedQuestion.answer} </span>
                </h6>
              </div>
            </div>
          )}
        </Modal>

        {/* Edit Question Modal */}
        <Modal
          title=""
          className="tab_modal"
          width={900}
          open={editOpen}
          onCancel={() => {
            setEditOpen(false);
            form.resetFields();
          }}
          footer={[false]}
        >
          <Form form={form} layout="vertical" onFinish={submitEditQuestion}>
            <Form.Item
              name="question"
              label="Question"
              rules={[{ required: true, message: "Please enter the question" }]}
            >
              <TextArea rows={4} placeholder="Enter question" />
            </Form.Item>
            <Form.Item
              name="answer"
              label="Answer"
              rules={[{ required: true, message: "Please enter the answer" }]}
            >
              <TextArea rows={6} placeholder="Enter answer" />
            </Form.Item>
          </Form>
          <div className="tab-modal-ft">
            <Button
              className="modal-footer-btn"
              loading={loading}
              onClick={() => form.submit()}
            >
              Save
            </Button>
          </div>
        </Modal>

        {/* Delete Question Modal */}
        <Modal
          title=""
          className="tab_modal"
          open={deleteOpen}
          onCancel={() => setDeleteOpen(false)}
          footer={[false]}
        >
          <div className="modal-inner-body">
            <img className="check-tital-icon" src={ModalDeleteIcon} />
            <h6>Are you sure you want to delete this?</h6>
            {/* <p>
              Lorem ipsum dolor sit amet consectetur. Venenatis amet id nunc
              risus risus.
            </p> */}
            <Button
              className="modal-fill-btn"
              type="primary"
              onClick={handleDeleteQuestion}
            >
              Continue
            </Button>
          </div>
        </Modal>
      </Card>
    </div>
  );
};

export default ListQuestionsIndex;
